import { useEffect, useRef, useState } from 'react';
import { blobToImagePage, type PdfImagePageItem } from './PdfImagesToPdfModal';
import { MAX_IMAGE_PAGES } from '@/lib/pdf/imagesToPdf';

interface PdfScanCameraModalProps {
  open: boolean;
  /** Pages already collected (for remaining slot count) */
  existingCount?: number;
  onClose: () => void;
  /** Called with newly captured pages when user taps Done */
  onDone: (pages: PdfImagePageItem[]) => void;
}

export default function PdfScanCameraModal({
  open,
  existingCount = 0,
  onClose,
  onDone,
}: PdfScanCameraModalProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const captureInputRef = useRef<HTMLInputElement>(null);

  const [facingMode, setFacingMode] = useState<'environment' | 'user'>('environment');
  const [captures, setCaptures] = useState<PdfImagePageItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [cameraReady, setCameraReady] = useState(false);

  const stopStream = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraReady(false);
  };

  useEffect(() => {
    if (!open) {
      stopStream();
      return;
    }

    setError(null);
    setCaptures([]);
    let cancelled = false;

    (async () => {
      try {
        stopStream();
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: facingMode },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        setCameraReady(true);
        setError(null);
      } catch (err) {
        console.warn('Camera unavailable', err);
        setCameraReady(false);
        setError(
          'Camera not available. Use Take photo (mobile) or Upload images instead.'
        );
      }
    })();

    return () => {
      cancelled = true;
      stopStream();
    };
  }, [open, facingMode]);

  // Attach stream after <video> mounts (cameraReady gate)
  useEffect(() => {
    if (!open || !cameraReady || !streamRef.current || !videoRef.current) return;
    const video = videoRef.current;
    video.srcObject = streamRef.current;
    void video.play().catch(() => undefined);
  }, [open, cameraReady, facingMode]);

  const revokeCaptures = (list: PdfImagePageItem[]) => {
    for (const p of list) URL.revokeObjectURL(p.previewUrl);
  };

  const handleClose = () => {
    revokeCaptures(captures);
    setCaptures([]);
    stopStream();
    onClose();
  };

  const remaining = MAX_IMAGE_PAGES - existingCount - captures.length;

  const captureFromVideo = async () => {
    const video = videoRef.current;
    if (!video || !cameraReady || remaining <= 0) return;

    const w = video.videoWidth;
    const h = video.videoHeight;
    if (!w || !h) {
      setError('Camera is not ready yet');
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, w, h);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.92)
    );
    if (!blob) {
      setError('Failed to capture photo');
      return;
    }

    const page = blobToImagePage(blob, `Scan-${captures.length + 1}.jpg`);
    setCaptures((prev) => [...prev, page]);
    setError(null);
  };

  const handleNativeCapture = (files: FileList | null) => {
    if (!files?.length) return;
    const file = files[0];
    if (!file.type.startsWith('image/') && !/\.(jpe?g|png|webp)$/i.test(file.name)) {
      setError('Please choose an image');
      return;
    }
    if (remaining <= 0) {
      setError(`Maximum ${MAX_IMAGE_PAGES} pages`);
      return;
    }
    const page = blobToImagePage(file, file.name || `Scan-${captures.length + 1}.jpg`);
    setCaptures((prev) => [...prev, page]);
    setError(null);
  };

  const handleDone = () => {
    if (!captures.length) {
      setError('Capture at least one page');
      return;
    }
    const batch = [...captures];
    setCaptures([]);
    stopStream();
    onDone(batch);
  };

  if (!open) return null;

  return (
    <div className="pdfe-modal-overlay" role="presentation" onClick={handleClose}>
      <div
        className="pdfe-modal pdfe-scan-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pdfe-scan-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="pdfe-modal-head">
          <h2 id="pdfe-scan-title">Scan document</h2>
          <button
            type="button"
            className="pdfe-modal-close"
            onClick={handleClose}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div className="pdfe-scan-body">
          <div className="pdfe-scan-video-wrap">
            <video
              ref={videoRef}
              playsInline
              muted
              autoPlay
              className="pdfe-scan-video"
              hidden={!cameraReady}
            />
            {!cameraReady && (
              <div className="pdfe-scan-video-fallback">
                <p>{error || 'Starting camera…'}</p>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => captureInputRef.current?.click()}
                >
                  Take photo
                </button>
              </div>
            )}
          </div>

          {captures.length > 0 && (
            <ul className="pdfe-scan-thumbs">
              {captures.map((c, i) => (
                <li key={c.id}>
                  <img src={c.previewUrl} alt={`Capture ${i + 1}`} />
                  <span>{i + 1}</span>
                </li>
              ))}
            </ul>
          )}

          {error && cameraReady && (
            <p className="pdfe-sig-modal-error">{error}</p>
          )}
        </div>

        <footer className="pdfe-modal-foot pdfe-scan-foot">
          <div className="pdfe-scan-foot-left">
            <button
              type="button"
              className="btn btn-secondary"
              disabled={!cameraReady}
              onClick={() =>
                setFacingMode((f) => (f === 'user' ? 'environment' : 'user'))
              }
            >
              Flip
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => captureInputRef.current?.click()}
            >
              Take photo
            </button>
          </div>
          <div className="pdfe-scan-foot-right">
            <button type="button" className="btn btn-secondary" onClick={handleClose}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!cameraReady || remaining <= 0}
              onClick={() => void captureFromVideo()}
            >
              Capture
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={captures.length === 0}
              onClick={handleDone}
            >
              Done ({captures.length})
            </button>
          </div>
        </footer>

        <input
          ref={captureInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          hidden
          onChange={(e) => {
            handleNativeCapture(e.target.files);
            e.target.value = '';
          }}
        />
      </div>
    </div>
  );
}
